import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { FieldConfig } from "@/config/forms/general";
import { Auction, Tirage, TirageInAuction } from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import TirageInAuctionDrawer from "./drawer/TirageInAuctionDrawer";

interface ComplexLinkProps {
  id: string;
  tirage: Tirage;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
  auction: Auction;
}

const columnsAuction = [
  {
    accessorKey: "auction.ventesDescriptionNumeroVente",
    header: "ventesDescriptionNumeroVente",
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return (
        <TextCell value={row.original.auction.ventesDescriptionNumeroVente} />
      );
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return (
        <ImageCell
          value={""}
          attachments={row.original.attachments ?? []}
          domain={"auction"}
        />
      );
    },
  },
  {
    accessorKey: "auction.ventesDescriptionCommissaires",
    header: "ventesDescriptionCommissaires",
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return (
        <TextCell value={row.original.auction.ventesDescriptionCommissaires} />
      );
    },
  },
  {
    accessorKey: "auction.ventesDescriptionDate",
    header: "ventesDescriptionDate",
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionDate} />;
    },
  },
  {
    accessorKey: "auction.ventesDescriptionVille",
    header: "ventesDescriptionVille",
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionVille} />;
    },
  },
  {
    accessorKey: "auction.ventesDescriptionPays",
    header: "ventesDescriptionPays",
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionPays} />;
    },
  },
];

const TirageInAuctionLinks = ({
  id,
  tirage,
  fields,
  type,
  auction,
}: ComplexLinkProps) => {
  const { t } = useTranslation();

  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "tirageInAuction",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const { mutate: deleteTirageInAuction } = useDeleteComplexLink(
    id,
    "tirageInAuction",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    async (id: string) => {
      await deleteTirageInAuction({
        id,
        relation: "tirageInAuction",
      });
    },
    [deleteTirageInAuction],
  );

  const columns: ColumnDef<TirageInAuction, any>[] = useMemo(() => {
    return [
      ...(type === "tirage"
        ? (columnsAuction.map((column) => ({
            ...column,
            header: typeof column.header === "string" ? t(column.header) : "",
          })) as ColumnDef<TirageInAuction, any>[])
        : []),
      {
        accessorKey: "tirageDansVentesDescriptionNumeroLot",
        header: t("auctionLotNumber"),
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<TirageInAuction> }) => {
          const data = row.original as TirageInAuction;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <TirageInAuctionDrawer
                key={row.original.id}
                fields={fields}
                data={data}
                queryId={id}
                type={type}
              />
              {type === "auction" && (
                <ShowElements
                  type="tirage"
                  id={row.original.tirage.id}
                  elements={[
                    {
                      label: t("tirageDescriptionNumeroEnregistrement"),
                      value:
                        row.original.tirage
                          .tirageDescriptionNumeroEnregistrement ?? "",
                      type: "text",
                    },
                    {
                      label: t("tirageDescriptionNotes"),
                      value: row.original.tirage.tirageDescriptionNotes ?? "",
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.tirage.attachments ?? [],
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "tirage" && (
                <ShowElements
                  type="auction"
                  id={row.original.auction.id}
                  elements={[
                    {
                      label: t("ventesDescriptionNumeroVente"),
                      value: row.original.auction.ventesDescriptionNumeroVente,
                      type: "text",
                    },
                    {
                      label: t("ventesDescriptionResume"),
                      value: row.original.auction.ventesDescriptionResume,
                      type: "richtext",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "tirage") {
      return (
        <TirageToAuctionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          tirage={tirage}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    } else {
      return (
        <AuctionToTirageLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          auction={auction}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    }
  }, [
    type,
    id,
    relatedData,
    fields,
    tirage,
    isAddMode,
    columns,
    searchContent,
    auction,
  ]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("relatedRecords")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">
          {type !== "tirage" ? t("tiragesRelated") : t("auctionsRelated")}
        </h2>
        {!isAddMode && (
          <Button variant="outline" onClick={() => setIsAddMode(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      {datatableConf}
    </div>
  );
};

type LinksTableProps<T> = {
  id: string;
  relatedData: T[];
  fields: FieldConfig[] | undefined;
  isAddMode: boolean;
  columnsLink: ColumnDef<T, any>[];
  searchContent: string;
};

type TirageToAuctionLinksProps = LinksTableProps<TirageInAuction> & {
  tirage: Tirage | undefined;
};

type AuctionToTirageLinksProps = LinksTableProps<TirageInAuction> & {
  auction: Auction | undefined;
};

const TirageToAuctionLinks = ({
  id,
  relatedData,
  fields,
  tirage,
  isAddMode,
  columnsLink,
  searchContent,
}: TirageToAuctionLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelatedAuctions } = useGetNotRelatedResource<Auction>(
    {
      resultType: "auction",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const columnsAuctions: ColumnDef<Auction, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "ventesDescriptionNumeroVente",
        header: t("ventesDescriptionNumeroVente"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionNumeroVente} />;
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Auction> }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"auction"}
            />
          );
        },
      },
      {
        accessorKey: "ventesDescriptionTitre",
        header: t("ventesDescriptionTitre"),
        size: 500,
        cell: (props) => {
          return <HtmlCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "ventesDescriptionDate",
        header: t("ventesDescriptionDate"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionDate} />;
        },
      },
      {
        accessorKey: "ventesDescriptionVille",
        header: t("ventesDescriptionVille"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionVille} />;
        },
      },
      {
        accessorKey: "ventesDescriptionPays",
        header: t("ventesDescriptionPays"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionPays} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Auction;
          if (!tirage) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="auction"
                id={row.original.id}
                elements={[
                  {
                    label: t("ventesDescriptionNumeroVente"),
                    value: row.original.ventesDescriptionNumeroVente,
                    type: "text",
                  },
                  {
                    label: t("ventesDescriptionResume"),
                    value: row.original.ventesDescriptionResume,
                    type: "richtext",
                  },
                ]}
              />
              <TirageInAuctionDrawer
                fields={fields}
                key={row.original.id}
                queryId={tirage.id}
                type={"tirage"}
                data={{
                  id: "",
                  auction: data,
                  tirage: tirage,
                  tirageDansVentesDescriptionNumeroLot: "",
                  tirageDansVentesDescriptionNotes: "",
                  tirageDansVentesDescriptionReproduitPage: "",
                  tirageDansVentesDescriptionEstimationHaute: "",
                  tirageDansVentesDescriptionEstimationBasse: "",
                  tirageDansVentesDescriptionVendu: false,
                  tirageDansVentesDescriptionResultatNonListe: false,
                  tirageDansVentesDescriptionNonVenu: false,
                  tirageDansVentesDescriptionNonVendu: false,
                  tirageDansVentesDescriptionNonReproduit: false,
                  tirageDansVentesDescriptionRetire: false,
                  tirageDansVentesDescriptionMiseAPrix: "",
                  tirageDansVentesDescriptionPrixMarteau: "",
                  tirageDansVentesDescriptionPrixAvecFrais: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [tirage, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsAuctions}
          data={notAlreadyRelatedAuctions?.data ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: notAlreadyRelatedAuctions?.totalSize ?? 0,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: 0,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      isAddMode,
      notAlreadyRelatedAuctions,
      relatedData,
      columnsAuctions,
      columnsLink,
      getPagination,
    ],
  );

  return datatableConf;
};

const AuctionToTirageLinks = ({
  id,
  relatedData,
  fields,
  auction,
  isAddMode,
  columnsLink,
  searchContent,
}: AuctionToTirageLinksProps) => {
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelatedTirages } = useGetNotRelatedResource<Tirage>({
    resultType: "tirage",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const { t } = useTranslation();
  const columnsTirages: ColumnDef<Tirage, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "tirageDescriptionNumeroEnregistrement",
        header: t("tirageDescriptionNumeroEnregistrement"),
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.tirageDescriptionNumeroEnregistrement ?? ""}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Tirage;
          if (!auction) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="tirage"
                id={row.original.id}
                elements={[
                  {
                    label: t("tirageDescriptionNumeroEnregistrement"),
                    value:
                      row.original.tirageDescriptionNumeroEnregistrement ?? "",
                    type: "text",
                  },
                  {
                    label: t("tirageDescriptionNotes"),
                    value: row.original.tirageDescriptionNotes ?? "",
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments ?? [],
                    type: "image",
                  },
                ]}
              />
              <TirageInAuctionDrawer
                fields={fields}
                key={row.original.id}
                queryId={auction.id}
                type={"auction"}
                data={{
                  id: "",
                  auction: auction,
                  tirage: data,
                  tirageDansVentesDescriptionNumeroLot: "",
                  tirageDansVentesDescriptionNotes: "",
                  tirageDansVentesDescriptionReproduitPage: "",
                  tirageDansVentesDescriptionEstimationHaute: "",
                  tirageDansVentesDescriptionEstimationBasse: "",
                  tirageDansVentesDescriptionVendu: false,
                  tirageDansVentesDescriptionResultatNonListe: false,
                  tirageDansVentesDescriptionNonVenu: false,
                  tirageDansVentesDescriptionNonVendu: false,
                  tirageDansVentesDescriptionNonReproduit: false,
                  tirageDansVentesDescriptionRetire: false,
                  tirageDansVentesDescriptionMiseAPrix: "",
                  tirageDansVentesDescriptionPrixMarteau: "",
                  tirageDansVentesDescriptionPrixAvecFrais: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [auction, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsTirages}
          data={notAlreadyRelatedTirages?.data ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: notAlreadyRelatedTirages?.totalSize ?? 0,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: 0,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsTirages,
      columnsLink,
      getPagination,
      isAddMode,
      notAlreadyRelatedTirages?.data,
      notAlreadyRelatedTirages?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};
export default TirageInAuctionLinks;
